import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import authService from '../services/authService';

export function useAuth() {
  const navigate = useNavigate();
  const {
    user,
    student,
    role,
    isAuthenticated,
    isLoading,
    error,
    loginAsTeacher,
    loginAsStudent,
    logout: storeLogout,
    selectStudent,
    setLoading,
    setError,
    clearError,
  } = useAuthStore();

  // Teacher login - email + password
  const login = useCallback(async (email, password) => {
    setLoading(true);
    clearError();

    try {
      const data = await authService.login(email, password);
      loginAsTeacher(data.user, data.access_token, data.refresh_token);
      navigate('/teacher'); // Teachers go to teacher dashboard
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed. Please try again.';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [navigate, loginAsTeacher, setLoading, setError, clearError]);

  // Student login - class code + name + PIN
  const studentLogin = useCallback(async (classCode, name, pin) => {
    setLoading(true);
    clearError();

    try {
      const data = await authService.studentLogin(classCode, name, pin);
      loginAsStudent(data.student, data.access_token, data.refresh_token);
      navigate('/app'); // Students go to student app
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed. Please check your credentials.';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [navigate, loginAsStudent, setLoading, setError, clearError]);

  // Teacher registration
  const register = useCallback(async (data) => {
    setLoading(true);
    clearError();

    try {
      const response = await authService.register(data);
      // Return the response but DON'T auto-navigate
      // Let the Register page show the class code first
      return {
        success: true,
        classCode: response.class_code,
        user: response.user,
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
      };
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed. Please try again.';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, clearError]);

  // Complete registration and navigate (called after user sees class code)
  const completeRegistration = useCallback((userData, token, refreshToken) => {
    loginAsTeacher(userData, token, refreshToken);
    navigate('/teacher');
  }, [navigate, loginAsTeacher]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (err) {
      // Ignore logout errors
    } finally {
      storeLogout();
      navigate('/');
    }
  }, [navigate, storeLogout]);

  const switchStudent = useCallback((student) => {
    selectStudent(student);
  }, [selectStudent]);

  return {
    user,
    student,
    role,
    isAuthenticated,
    isTeacher: role === 'teacher',
    isStudentUser: role === 'student',
    isLoading,
    error,
    login,
    studentLogin,
    register,
    completeRegistration,
    logout,
    switchStudent,
    clearError,
  };
}

export default useAuth;
